/**
 * Steps to revert temp support for vanilla lesson templates
 * 1. Remove import of LessonPage to TempLessonPage
 * 2. uncomment the import of LessonView to TempLessonView
 * 3. Delete the LessonPage.js file
 * 4. Delete the useLessonPage.js file
 * 5. Change { path: ROUTES.LESSON, component: TempLessonPage }, to { path: ROUTES.LESSON, component: LessonView },
 */

import { TITLE_KEYS } from 'app/router/routeTitleKeys';
import { TEACHER_DASHBOARD_FEATURES } from 'core/chatbot/constants/teacherDashboardFeatures';
import { isTeams } from 'core/utils/teamsTab';
import { ROUTES } from 'edelvives/utils/constants/paths';
import React from 'react';

import DemoLoading from 'edelvives/pages/DemoLandingPage/DemoLoading';
// import LogoutPage from '../modules/containers/Logout';
import AboutView from '../modules/views/AboutView/AboutView';
import SupportView from '../modules/views/SupportView/SupportView';
import AIPolitics from '../pages/StaticPages/AIPolitics';
import PrivacyPolicy from '../pages/StaticPages/PrivacyPolicy';
import TermsOfUse from '../pages/StaticPages/TermsOfUse';
import TabAuthEnd from '../pages/Teams/TabAuthEnd';
import TabAuthStart from '../pages/Teams/TabAuthStart';
import LoginPage from '../pages/auth/Login/LoginPage';
import SignUpPage from '../pages/auth/SignUp/SignUpPage';

const ErrorView = React.lazy(() => import('edelvives/modules/views/ErrorView/ErrorView'));
const HomePage = React.lazy(() => import('edelvives/pages/HomePage/HomePage'));
const KanbanPage = React.lazy(() => import('edelvives/pages/KanbanPage/KanbanPage'));
const CoursePage = React.lazy(() => import('edelvives/pages/CoursePage/CoursePage'));
const TrainingPracticePage = React.lazy(() => import('app/pages/TrainingPracticePage/TrainingPracticePage'));
const LessonPage = React.lazy(() => import('edelvives/pages/LessonPage/LessonPage'));
// eslint-disable-next-line no-unused-vars
const LessonView = React.lazy(() => import('core/lite/views/LessonView/LessonView/LessonView'));
const CustomLessonView = React.lazy(() => import('edelvives/modules/views/CustomLessonView/CustomLessonView'));
const CustomLessonViewToDigest = React.lazy(() => import('edelvives/modules/views/CustomLessonView/CustomLessonViewToDigest'));
const LogoutPage = React.lazy(() => import('edelvives/modules/containers/Logout'));
const ToolsProjectContainer = React.lazy(() => import('core/lite/views/ToolsProjectView/ToolsProjectContainer'));
const AnswerTest = React.lazy(() => import('../pages/AnswerTest'));
const AssessmentResult = React.lazy(() => import('../pages/AssessmentResultPage/AssessmentResult'));
const TokenCallback = React.lazy(() => import('../pages/auth/TokenCallback'));

const NewActivityPage = React.lazy(() => import('app/pages/NewActivityPage/NewActivityPage.js'));
const CalendarPage = React.lazy(() => import('../pages/CalendarPage/CalendarPage'));
const LemonTest = React.lazy(() => import('../pages/LemonTest'));
// const NewContentView = React.lazy(() => import('../pages/NewContent'));
const ProfilePage = React.lazy(() => import('../pages/ProfilePage/ProfilePage'));
const ProjectionMint = React.lazy(() => import('../pages/ProjectionMint'));
const Projection = React.lazy(() => import('../pages/Projection'));
const RemotePage = React.lazy(() => import('../pages/RemotePage'));
const ViewerRouting = React.lazy(() => import('../containers/ViewerContainer/ViewerRouting'));
const ViewerFullDemo = React.lazy(() => import('app/containers/ViewerContainer/ViewerFullDemo'));
const AddLicense = React.lazy(() => import('../pages/AddLicensePage/AddLicensePage'));
const RecoverPasswordPage = React.lazy(() => import('../pages/RecoverPasswordPage/RecoverPasswordPage'));
const ClassroomUsersPage = React.lazy(() => import('../pages/ClassroomUsersPage/ClassroomUsersPage'));
const GroupResourcesPage = React.lazy(() => import('../pages/GroupResourcesPage/GroupResourcesPage'));
const MSTeamsConfiguration = React.lazy(() => import('../pages/MSTeamsConfiguration'));
const IndividualActivityPage = React.lazy(() => import('../pages/IndividualActivityPage/IndividualActivityPage'));
const DeepLinkView = React.lazy(() => import('core/lite/views/DeepLinkView'));
const DialogDesktopVersion = React.lazy(() => import('ui/shared/dialogs/DialogDesktopVersion/DialogDesktopVersion'));
// const DemoLandingPage = React.lazy(() => import('edelvives/pages/DemoEsoLandingPage/DemoEsoLandingPage'));
const CreateEvaluationsPage = React.lazy(() => import('../pages/CreateEvaluationsPage'));
const CreateEvaluationsIAPage = React.lazy(() => import('../pages/CreateEvaluationsIAPage'));
const DoEvaluationPage = React.lazy(() => import('../pages/DoEvaluationPage'));
const ViewEvaluationPage = React.lazy(() => import('../pages/ViewEvaluationPage'));
const ResultsEvaluationPage = React.lazy(() => import('../pages/ResultsEvaluationPage'));
const ResourcesPage = React.lazy(() => import('edelvives/pages/ResourcesPage/ResourcesPage'));
const TasksPage = React.lazy(() => import('edelvives/pages/TasksPage'));
const AssessmentPage = React.lazy(() => import('edelvives/pages/AssessmentPage'));
const ResetPasswordPage = React.lazy(() => import('../pages/ResetPasswordPage/ResetPasswordPage'));
const ChatbotPracticePage = React.lazy(() => import('edelvives/pages/ChatbotPracticePage'));
const RadarPage = React.lazy(() => import('app/pages/RadarPage/RadarPage'));
const TodayPage = React.lazy(() => import('app/pages/TodayPage/TodayPage'));
const MyProgressPage = React.lazy(() => import('app/pages/MyProgressPage/MyProgressPage'));
const SubjectDetailPage = React.lazy(() => import('app/pages/SubjectDetailPage/SubjectDetailPage'));
const StudentKcProgressPage = React.lazy(() => import('app/pages/StudentKcProgressPage/StudentKcProgressPage'));
const StudentSkillsProgressPage = React.lazy(() => import('app/pages/StudentSkillsProgressPage/StudentSkillsProgressPage'));
const TeacherAnalyticsStudentsListPage = React.lazy(() => import('app/pages/TeacherAnalyticsStudentsListPage/TeacherAnalyticsStudentsListPage'));
const AnalyticsPage = React.lazy(() => import('app/pages/AnalyticsPage/AnalyticsPage'));
// const TeacherAnalyticsPage = React.lazy(() => import('core/chatbot/views/TeacherAnalyticsView/TeacherAnalyticsView')); // Deprecated
const TeacherToolsPage = React.lazy(() => import('core/chatbot/views/TeacherToolsView/TeacherToolsView'));
const TeacherToolsHistoryView = React.lazy(() => import('core/chatbot/views/TeacherToolsHistoryView/TeacherToolsHistoryView'));
const ToolInstanceChatView = React.lazy(() => import('core/chatbot/views/ToolInstanceChatView/ToolInstanceChatView'));
const GenericToolSetup = React.lazy(() => import('core/chatbot/components/tools/GenericToolSetup/GenericToolSetup'));
const EvalsDashboardPage = React.lazy(() => import('app/pages/EvalsDashboardPage/EvalsDashboardPage'));

export const externalRoutes = [
  { path: ROUTES.REMOTE_TOKEN, component: RemotePage, titleKey: TITLE_KEYS.Redirecting },
  { path: ROUTES.DEEPLINK, component: DeepLinkView, titleKey: TITLE_KEYS.Redirecting },
  { path: ROUTES.DEEPLINK_TOKEN, component: DeepLinkView, titleKey: TITLE_KEYS.Redirecting },
  { path: ROUTES.ABOUT, component: AboutView, titleKey: TITLE_KEYS.About },
  { path: ROUTES.SUPPORT, component: SupportView, titleKey: TITLE_KEYS.Support },
  /* Teams configuration is required regardless if authorized or not to be able to bypass the popup */
  { path: ROUTES.TEAMS_CONFIG, component: MSTeamsConfiguration, titleKey: TITLE_KEYS.TeamsConfig },
  { path: ROUTES.TEAMS_TAB_AUTH_START, component: TabAuthStart, titleKey: TITLE_KEYS.Redirecting },
  { path: ROUTES.TEAMS_TAB_AUTH_END, component: TabAuthEnd, titleKey: TITLE_KEYS.Redirecting },
  /* Static pages */
  { path: ROUTES.PRIVACY, component: PrivacyPolicy, titleKey: TITLE_KEYS.Privacy },
  { path: ROUTES.TERMS, component: TermsOfUse, titleKey: TITLE_KEYS.Terms },
  { path: ROUTES.IA, component: AIPolitics, titleKey: TITLE_KEYS.AIPolicy },
  { path: ROUTES.LOGOUT, component: LogoutPage, titleKey: TITLE_KEYS.Redirecting },
  { path: ROUTES.LOADING_DEMO, component: DemoLoading, titleKey: TITLE_KEYS.Redirecting },
  { path: ROUTES.PRESCHOOL_RESOURCES, component: ResourcesPage, titleKey: TITLE_KEYS.PreschoolResources },
];

export const publicRoutes = [
  { path: ROUTES.AUTH_LOGIN_TOKEN, component: TokenCallback, titleKey: TITLE_KEYS.Redirecting },
  { path: ROUTES.AUTH_LOGIN, component: LoginPage, titleKey: TITLE_KEYS.Login },
  // `ROUTES.AUTH_SIGNUP` no existe en las constantes de Edelvives (la ruta es `SIGNUP`): con el
  // nombre anterior el `path` era `undefined`. Ver `Routes.js`, que pinta `ROUTES.SIGNUP`.
  { path: ROUTES.SIGNUP, component: SignUpPage, titleKey: TITLE_KEYS.Signup },
  { path: ROUTES.RECOVER_PASSWORD, component: RecoverPasswordPage, titleKey: TITLE_KEYS.RecoverPassword },
];

export const localhostRoutes = [
  // { path: ROUTES.TEST_COMPONENT, component: TestComponentView },
];

export const devModeRoutes = [{ path: ROUTES.EVALS_DASHBOARD, component: EvalsDashboardPage, titleKey: TITLE_KEYS.EvalsDashboard }];

export const privateRoutes = [
  { path: ROUTES.HOME, component: HomePage, titleKey: TITLE_KEYS.Home },
  // "Today" student space — no `feature:` on purpose (fail-open); the entry points
  // (home banner and "My space" header button) own the AI + student gating.
  { path: ROUTES.MY_PROGRESS_SUBJECT, exact: true, component: SubjectDetailPage, titleKey: TITLE_KEYS.ProgressSubjectDetail, isTitleOwnedByPage: true },
  { path: ROUTES.TODAY, exact: true, component: TodayPage, titleKey: TITLE_KEYS.Today },
  // "Mi progreso" student space — same fail-open gating as Today (no `feature:`); reached
  // from the "Mi progreso" sidebar item, which is only shown inside the student space.
  { path: ROUTES.MY_PROGRESS, exact: true, component: MyProgressPage, titleKey: TITLE_KEYS.MyProgress },
  { path: ROUTES.ASSESSMENT_QUESTION, component: LemonTest, titleKey: TITLE_KEYS.Question },
  { path: ROUTES.ASSESSMENT_RESULT, component: AssessmentResult, titleKey: TITLE_KEYS.Results, isTitleOwnedByPage: true },
  // Sin `isTitleOwnedByPage`: el nombre del quiz vive dos niveles por debajo (AssessmentPage delega en
  // envoltorios que a su vez montan la vista que lo pide), y el registro solo llega un nivel. Se queda
  // con el título genérico de la ruta antes que pedir el nombre otra vez solo para titular.
  { path: ROUTES.ASSESSMENT, component: AssessmentPage, titleKey: TITLE_KEYS.Assessment },
  // { path: ROUTES.NEW_CONTENT, component: NewContentView },
  // { path: ROUTES.CONTENT, component: NewContentView },
  { path: ROUTES.PROJECTION_MINT, component: ProjectionMint, titleKey: TITLE_KEYS.Projection },
  { path: ROUTES.PROJECTION, component: Projection, titleKey: TITLE_KEYS.Projection },
  // Quiz creation from program home (TAN-8587) navigates to the lite activity editor
  { path: ROUTES.ADD_ITEM, component: NewActivityPage, titleKey: TITLE_KEYS.NewActivity, isTitleOwnedByPage: true },
  { path: ROUTES.LESSON, component: LessonPage, titleKey: TITLE_KEYS.Lesson, isTitleOwnedByPage: true },
  {
    path: ROUTES.CUSTOM_LESSON_DIGEST,
    component: CustomLessonViewToDigest,
    props: { typeToFilterLesson: isTeams() ? 'mint' : null },
    titleKey: TITLE_KEYS.Lesson,
    isTitleOwnedByPage: true,
  },
  { path: ROUTES.CUSTOM_LESSON, component: CustomLessonView, props: { typeToFilterLesson: isTeams() ? 'mint' : null }, titleKey: TITLE_KEYS.Lesson, isTitleOwnedByPage: true },
  { path: ROUTES.CUSTOM_LESSON_UNIT, component: CustomLessonView, props: { typeToFilterLesson: isTeams() ? 'mint' : null }, titleKey: TITLE_KEYS.Lesson, isTitleOwnedByPage: true },
  // Crear y editar (con y sin IA) son la misma pantalla y llevan el mismo encabezado,
  // «Generador de pruebas»: un único título estático, sin dato que aportar.
  { path: ROUTES.CREATE_EVALUATION, component: CreateEvaluationsPage, titleKey: TITLE_KEYS.EvaluationsGenerator },
  { path: ROUTES.CREATE_EVALUATION_IA, component: CreateEvaluationsIAPage, titleKey: TITLE_KEYS.EvaluationsGenerator },
  { path: ROUTES.EDIT_EVALUATION, component: CreateEvaluationsPage, titleKey: TITLE_KEYS.EvaluationsGenerator },
  { path: ROUTES.EDIT_EVALUATION_IA, component: CreateEvaluationsIAPage, titleKey: TITLE_KEYS.EvaluationsGenerator },
  { path: ROUTES.EVALUATION, component: DoEvaluationPage, titleKey: TITLE_KEYS.Evaluation, isTitleOwnedByPage: true },
  { path: ROUTES.EVALUATION_VIEW_RESOURCES, component: ViewEvaluationPage, titleKey: TITLE_KEYS.Evaluation, isTitleOwnedByPage: true },
  { path: ROUTES.EVALUATION_VIEW, component: ViewEvaluationPage, titleKey: TITLE_KEYS.Evaluation, isTitleOwnedByPage: true },
  { path: ROUTES.EVALUATION_RESULTS_RESOURCES, component: ResultsEvaluationPage, titleKey: TITLE_KEYS.Results, isTitleOwnedByPage: true },
  { path: ROUTES.EVALUATION_RESULTS, component: ResultsEvaluationPage, titleKey: TITLE_KEYS.Results, isTitleOwnedByPage: true },
  { path: ROUTES.KANBAN_LESSON, component: KanbanPage, titleKey: TITLE_KEYS.Kanban, isTitleOwnedByPage: true },
  { path: ROUTES.COURSE_GRADES, component: CoursePage, props: { tabId: 'grades' }, titleKey: TITLE_KEYS.CourseGrades, isTitleOwnedByPage: true },
  { path: ROUTES.COURSE_PROGRAM, component: CoursePage, props: { tabId: 'program' }, titleKey: TITLE_KEYS.CourseProgram, isTitleOwnedByPage: true },
  { path: ROUTES.COURSE_TRAINING_PRACTICE, component: TrainingPracticePage, titleKey: TITLE_KEYS.TrainingPractice, isTitleOwnedByPage: true },
  { path: ROUTES.COURSE_TRAINING_SECTION, component: CoursePage, props: { tabId: 'training' }, titleKey: TITLE_KEYS.CourseTraining, isTitleOwnedByPage: true },
  { path: ROUTES.COURSE_TRAINING, component: CoursePage, props: { tabId: 'training' }, titleKey: TITLE_KEYS.CourseTraining, isTitleOwnedByPage: true },
  { path: ROUTES.COURSE_HOME, component: CoursePage, props: { tabId: 'home' }, titleKey: TITLE_KEYS.CourseHome, isTitleOwnedByPage: true },
  { path: ROUTES.RESOURCES_PAGE, component: GroupResourcesPage, titleKey: TITLE_KEYS.CourseResources, isTitleOwnedByPage: true },
  { path: ROUTES.COURSE_RESOURCES, component: CoursePage, props: { tabId: 'resources' }, titleKey: TITLE_KEYS.CourseResources, isTitleOwnedByPage: true },
  {
    path: ROUTES.COURSE_RESOURCES_DEMO,
    component: CoursePage,
    props: { tabId: 'resources-demo' },
    titleKey: TITLE_KEYS.CourseResources,
    isTitleOwnedByPage: true,
  },
  { path: ROUTES.COURSE, component: CoursePage, props: { tabId: 'stream' }, titleKey: TITLE_KEYS.CourseStream, isTitleOwnedByPage: true },
  { path: ROUTES.VIEWER_FULL, component: ViewerRouting, props: { showHeader: false }, titleKey: TITLE_KEYS.Viewer, isTitleOwnedByPage: true },
  { path: ROUTES.VIEWER, component: ViewerRouting, props: { showHeader: true }, titleKey: TITLE_KEYS.Viewer, isTitleOwnedByPage: true },
  { path: ROUTES.VIEWER_FULL_GROUP, component: ViewerRouting, props: { showHeader: false }, titleKey: TITLE_KEYS.Viewer, isTitleOwnedByPage: true },
  { path: ROUTES.VIEWER_GROUP, component: ViewerRouting, props: { showHeader: true }, titleKey: TITLE_KEYS.Viewer, isTitleOwnedByPage: true },
  { path: ROUTES.VIEWER_FULL_DEMO, component: ViewerFullDemo, titleKey: TITLE_KEYS.Viewer, isTitleOwnedByPage: true },
  { path: ROUTES.CALENDAR, component: CalendarPage, titleKey: TITLE_KEYS.Calendar },
  { path: ROUTES.CALENDAR_COURSE, component: CalendarPage, titleKey: TITLE_KEYS.Calendar },
  { path: ROUTES.PROFILE, component: ProfilePage, titleKey: TITLE_KEYS.Profile },
  { path: ROUTES.TASKS, component: TasksPage, titleKey: TITLE_KEYS.Tasks },
  { path: ROUTES.LEMON_TEST, component: LemonTest, titleKey: TITLE_KEYS.Question },
  { path: ROUTES.ANSWER_TEST, component: AnswerTest, titleKey: TITLE_KEYS.Assessment },
  { path: ROUTES.SINGLE_ACTIVITY_RECORD, component: IndividualActivityPage, props: { record: true }, titleKey: TITLE_KEYS.Activity },
  { path: ROUTES.SINGLE_ACTIVITY, component: IndividualActivityPage, titleKey: TITLE_KEYS.Activity },
  { path: ROUTES.CLASSROOM_USERS, component: ClassroomUsersPage, titleKey: TITLE_KEYS.ClassroomUsers },
  { path: ROUTES.TOOLS, component: ToolsProjectContainer, titleKey: TITLE_KEYS.Tools },
  { path: ROUTES.REMOTE, component: RemotePage, titleKey: TITLE_KEYS.Redirecting },
  { path: ROUTES.ERROR, component: ErrorView, titleKey: TITLE_KEYS.Error },
  { path: ROUTES.LICENSE, component: AddLicense, titleKey: TITLE_KEYS.License },
  { path: ROUTES.DESKTOP, component: DialogDesktopVersion, titleKey: TITLE_KEYS.Desktop },
  { path: ROUTES.RESET_PASSWORD, component: ResetPasswordPage, titleKey: TITLE_KEYS.ResetPassword },
  { path: ROUTES.CHATBOT_PRACTICE_SESSION_SUMMARY, component: ChatbotPracticePage, titleKey: TITLE_KEYS.Practice },
  { path: ROUTES.CHATBOT_PRACTICE_SESSION_ACTIVITY, component: ChatbotPracticePage, titleKey: TITLE_KEYS.Practice },
  { path: ROUTES.STUDENT_KC_PROGRESS_LESSON, component: StudentKcProgressPage, feature: TEACHER_DASHBOARD_FEATURES.RADAR, titleKey: TITLE_KEYS.StudentKcProgress },
  { path: ROUTES.STUDENT_KC_PROGRESS, component: StudentKcProgressPage, feature: TEACHER_DASHBOARD_FEATURES.RADAR, titleKey: TITLE_KEYS.StudentKcProgress },
  {
    path: ROUTES.STUDENT_SKILLS_PROGRESS,
    component: StudentSkillsProgressPage,
    feature: TEACHER_DASHBOARD_FEATURES.RADAR,
    titleKey: TITLE_KEYS.StudentProgress,
  },
  { path: ROUTES.STUDENTS_LIST, component: TeacherAnalyticsStudentsListPage, feature: TEACHER_DASHBOARD_FEATURES.RADAR, titleKey: TITLE_KEYS.Students },
  { path: ROUTES.RADAR, component: RadarPage, feature: TEACHER_DASHBOARD_FEATURES.RADAR, titleKey: TITLE_KEYS.Radar },
  // { path: ROUTES.TEACHER_ANALYTICS, component: TeacherAnalyticsPage }, // Deprecated
  { path: ROUTES.TEACHER_ANALYTICS_BASE, component: AnalyticsPage, feature: TEACHER_DASHBOARD_FEATURES.ANALYTICS_PANEL, titleKey: TITLE_KEYS.Analytics },
  {
    path: ROUTES.TEACHER_TOOLS_HISTORY,
    component: TeacherToolsHistoryView,
    feature: TEACHER_DASHBOARD_FEATURES.AI_TOOLS,
    titleKey: TITLE_KEYS.TeacherToolsHistory,
  },

  // Use the Chat-based split view for tool instances
  {
    path: ROUTES.TEACHER_TOOL_INSTANCE,
    component: ToolInstanceChatView,
    feature: TEACHER_DASHBOARD_FEATURES.AI_TOOLS,
    titleKey: TITLE_KEYS.TeacherTool,
    isTitleOwnedByPage: true,
  },

  // Teacher Tools Setup Routes - Single parameterized route for all tools
  {
    path: '/teacher-tools/setup/:toolId',
    component: GenericToolSetup,
    feature: TEACHER_DASHBOARD_FEATURES.AI_TOOLS,
    titleKey: TITLE_KEYS.TeacherToolSetup,
    isTitleOwnedByPage: true,
  },

  // Teacher Tools Main Routes
  { path: ROUTES.TEACHER_TOOLS, exact: true, component: TeacherToolsPage, feature: TEACHER_DASHBOARD_FEATURES.AI_TOOLS, titleKey: TITLE_KEYS.TeacherTools },
];
